import React, { useContext, useEffect, useMemo, useState } from 'react';
import { applyDesignTheme } from '../components/utilsDesign';
import {
  fetchUncertainDesigns,
  getUncertainCacheKey,
  getUncertainDobs,
  isUncertainAvatar,
} from '../components/utilsUncertain';

const UncertainContext = React.createContext({
  available: false,
  loading: false,
  designs: null,
  dobs: null,
  common: null,
  potential: null,
  commonGates: [],
  commonCenters: [],
  changingPlanets: [],
  pageDesign: null,
  main: 'mid',
  setMain: () => {},
});

const autoSwitchedKeys = new Set();

export function UncertainProvider({
  avatar,
  isMe = false,
  children,
}) {
  const available = isUncertainAvatar(avatar, isMe);
  const cacheKey = getUncertainCacheKey(avatar, isMe);

  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [main, setMain] = useState('mid');

  useEffect(() => {
    if (!available || !cacheKey) {
      setBundle(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    const mid = avatar?.design?.id && avatar.design.id !== 'dummy' ? avatar.design : null;
    const dobs = getUncertainDobs(avatar);
    if (mid) {
      setBundle({
        designs: { start: null, mid, end: null },
        dobs,
        common: mid,
        potential: null,
        commonGates: [],
        commonCenters: [],
        changingPlanets: [],
      });
    }
    setLoading(true);
    fetchUncertainDesigns(avatar).then((next) => {
      if (cancelled) return;
      setBundle(next);
      setLoading(false);
    }).catch((error) => {
      console.error('Error loading uncertain designs:', error);
      if (cancelled) return;
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [available, cacheKey]);

  useEffect(() => {
    if (!available || !cacheKey) return;
    if (autoSwitchedKeys.has(cacheKey)) return;
    autoSwitchedKeys.add(cacheKey);
    setMain('mid');
  }, [available, cacheKey]);

  const pageDesign = (() => {
    if (!available) return avatar?.design;
    if (main !== 'uncertain' && bundle?.designs?.[main]) return bundle.designs[main];
    if (bundle?.common?.type) return bundle.common;
    return avatar?.design;
  })();

  useEffect(() => {
    if (!available) return;
    applyDesignTheme(pageDesign);
  }, [available, pageDesign]);

  const value = useMemo(() => ({
    available,
    loading,
    designs: bundle?.designs || null,
    dobs: bundle?.dobs || null,
    common: bundle?.common || null,
    potential: bundle?.potential || null,
    commonGates: bundle?.commonGates || [],
    commonCenters: bundle?.commonCenters || [],
    changingPlanets: bundle?.changingPlanets || [],
    pageDesign,
    main,
    setMain,
  }), [available, loading, bundle, main, pageDesign]);

  return (
    <UncertainContext.Provider value={value}>
      {children}
    </UncertainContext.Provider>
  );
}

export function useUncertainPageDesign(avatar) {
  const uncertain = useContext(UncertainContext);
  if (uncertain?.pageDesign) return uncertain.pageDesign;
  return avatar?.design;
}

export { UncertainContext };
